import { useState } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { RATIO_SIZE, type Ratio } from '@orisini/studio-kit/ratios'
import PosterPreview from '../../content/components/PosterPreview'
import { brandApi, type TemplateInfo } from '../lib/brand'
import { useTemplateDocs } from '../lib/use-template-docs'

function when(ms: number): string {
  return new Date(ms).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
}

/**
 * Template hasil kompilasi satu target (poster, atau satu style carousel):
 * status, preview dengan isi contoh, dan tombol bangun ulang.
 */
export function TemplateSection({
  workspaceId,
  info,
  ratios,
  onChanged,
}: {
  workspaceId: string
  info: TemplateInfo
  ratios: readonly Ratio[]
  onChanged: () => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const preview = useTemplateDocs(info.def, ratios)

  const bangun = async () => {
    setBusy(true)
    try {
      const r = await brandApi.compile(workspaceId, info.kind, info.style ?? undefined)
      if (r.ok) toast.success('Design rebuilt from your rules')
      else toast.error("The AI's design didn't pass our checks, so the previous one is kept. Try again or adjust the rules.")
      await onChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't rebuild the design")
    } finally {
      setBusy(false)
    }
  }

  const status =
    info.source === 'default'
      ? 'Using the default design — build one from your rules.'
      : info.upToDate
        ? `Built from your current rules · version ${info.version} · ${when(info.compiledAt!)}`
        : `Your rules changed since this was built (version ${info.version}). Rebuild to apply them.`

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Design</h3>
          <p className={`text-xs ${info.source === 'compiled' && !info.upToDate ? 'text-amber-400' : 'text-muted-foreground'}`}>{status}</p>
        </div>
        <Button size="sm" variant={info.source === 'compiled' && info.upToDate ? 'outline' : 'default'} disabled={busy} onClick={bangun}>
          {busy ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          {busy ? 'Building… (up to a minute)' : info.source === 'default' ? 'Build design' : 'Rebuild'}
        </Button>
      </div>

      {info.lastError && (
        <details className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
          <summary className="flex cursor-pointer items-center gap-1.5 text-amber-400">
            <AlertTriangle className="size-3.5" /> The last build on {when(info.lastError.at)} was rejected
          </summary>
          <pre className="mt-2 whitespace-pre-wrap text-muted-foreground">{info.lastError.message}</pre>
        </details>
      )}

      {preview.error ? (
        <p className="text-xs text-destructive">{preview.error}</p>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          {preview.docs.map((d) => (
            <PosterPreview
              key={d.key}
              doc={d.doc}
              width={RATIO_SIZE[d.ratio].width}
              height={RATIO_SIZE[d.ratio].height}
              displayWidth={info.kind === 'poster' ? 150 : 170}
              label={d.label}
            />
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">Preview with sample text. Real posts use the story's own words and photo.</p>
    </section>
  )
}
